import { ISurveyQuestionGroupItem } from '../../services/survey/survey.model';

/** Answer fields a rule input can read. Derived from the question type; never chosen by the user. */
export const INPUT_PATHS = [
  'numericValue',
  'textValue',
  'textValueAsDate',
  'booleanValue',
  'moreDetailsText',
  'jsonValue',
] as const;

/** Plain-language name for a stored answer field, for the rare legacy rule that reads a non-default one. */
export function pathLabel(path: string | undefined): string {
  switch (path) {
    case 'numericValue': return 'number';
    case 'textValue': return 'text';
    case 'textValueAsDate': return 'date';
    case 'booleanValue': return 'yes/no answer';
    case 'moreDetailsText': return 'more details text';
    case 'jsonValue': return 'raw answer data';
    default: return path ?? 'answer';
  }
}

/**
 * Why this question cannot feed a calculation, or null when it can.
 * Text questions need "numeric answer" turned on; choice questions need a
 * calculation value on every option.
 */
export function numericGroupItemError(item: ISurveyQuestionGroupItem): string | null {
  const code = item.surveyQuestion?.surveyQuestionType?.code;
  if (code === 'TEXT_SINGLE_LINE' || code === 'TEXT_MULTI_LINE') {
    if (item.surveyQuestion?.enforceNumericAnswer !== true) {
      return 'This question cannot be used in a calculation as it does not produce a numeric value';
    }
    return null;
  }
  if (code === 'SELECT_SINGLE' || code === 'SELECT_MULTI' || code === 'RADIO' || code === 'CHECKBOX_GROUP') {
    const opts = item.surveyQuestion?.surveyQuestionOptions ?? [];
    const allHaveNumeric = opts.length > 0 && opts.every(
      o => o.numericValue != null && Number.isFinite(Number(o.numericValue)),
    );
    if (!allHaveNumeric) {
      return 'This question cannot be used in a calculation as some options do not have a calculation value';
    }
    return null;
  }
  return null;
}

export function isNumericGroupItem(item: ISurveyQuestionGroupItem): boolean {
  return numericGroupItemError(item) === null;
}

export function firstNumericItem(items: ISurveyQuestionGroupItem[]): ISurveyQuestionGroupItem | undefined {
  return items.find(i => numericGroupItemError(i) === null);
}

export function defaultPathForQuestion(item: ISurveyQuestionGroupItem | undefined): string {
  return item?.surveyQuestion?.surveyQuestionType?.code === 'DATE'
    ? 'textValueAsDate'
    : 'numericValue';
}
